<template>
    <div>
        <h5 v-if="localValue.length">
            {{
                localValue.length > 1
                    ? $t('fields.upload.files')
                    : $t('fields.upload.file')
            }}
        </h5>

        <div v-for="(file, index) in localValue" :key="fileKey(file)">
            <!-- TODO: Finish -->
            <div
                class="d-flex justify-content-between align-items-center mb-2 gap-2"
            >
                <div class="file-name text-truncate">
                    {{ fileDisplayName(file) }}
                </div>

                <div class="d-flex">
                    <IconButton
                        v-if="!deferred && !hideViewButton"
                        preset="view"
                        class="me-2"
                        @click="viewFile(file)"
                    />
                    <IconButton
                        v-if="!deferred && downloadFileHandler"
                        preset="download"
                        class="me-2"
                        @click="downloadFile(file)"
                    />
                    <IconButton
                        v-if="!hideDeleteButton"
                        preset="delete"
                        @click="removeFile(file, index)"
                    />
                </div>
            </div>
        </div>

        <ToolTip full-width>
            <template #tooltip-trigger>
                <UploadField
                    v-if="!hideUploadField"
                    :accept="accept"
                    :multiple="multiple"
                    :prevent-upload="deferred"
                    :max-image-height="maxImageHeight"
                    :max-image-width="maxImageWidth"
                    :max-file-size-mb="maxFileSizeMb"
                    :disabled="disabled"
                    :allow-public-access="allowPublicAccess"
                    v-on:uploaded="fileUploaded"
                    v-on:upload="deferredFileSelected"
                    v-on:uploading="uploading = $event"
                >
                    <template #image-preview>
                        <slot name="image-preview"></slot>
                    </template>
                </UploadField>
            </template>
            <template v-if="disabled && disabledTooltip" #tooltip-content>
                {{ disabledTooltip }}
            </template>
        </ToolTip>

        <!-- Delete Employee Modal -->
        <ConfirmModal
            :visible="isModalVisible"
            :title="$t(`${deleteModalTranslationKey}.title`)"
            :confirm-button-text="
                $t(`${deleteModalTranslationKey}.confirmButton`)
            "
            @close="closeModal"
            @confirm="deleteImage"
        >
            {{ $t(`${deleteModalTranslationKey}.descriptionPrefix`) }}
            <span class="fw-bold text-break">{{
                selectedFile.original_filename
            }}</span
            >{{ $t(`${deleteModalTranslationKey}.descriptionSuffix`) }}
        </ConfirmModal>
    </div>
</template>

<script>
import UploadField from '@/components/fields/UploadField.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import IconButton from '@/components/IconButton.vue';
import ToolTip from './ToolTip.vue';
import { getLibraryFileAction } from '@/components/library/utils/libraryFileHandling.js';

/**
 * Upload modes:
 * - Immediate mode (default) uploads through the legacy upload service as soon
 *   as a user selects a file. The model contains persisted file records.
 * - Deferred mode keeps raw File objects in the model. The owning form must
 *   persist them later, usually in the same multipart request as its metadata.
 *
 * Deferred mode never calls upload or delete endpoints. Removing a deferred
 * file only updates the local model, so cancelling the form leaves no orphaned
 * uploads.
 */

export default {
    name: 'AdvancedUploadField',
    components: {
        UploadField,
        ConfirmModal,
        IconButton,
        ToolTip,
    },
    props: {
        modelValue: {
            // Files
            type: null,
            default: [],
        },
        accept: {
            // 'image', '*' (any), or list of specific types (ex. ["jpg", "jpeg", "png"])
            type: [String, Array],
            default: '*',
        },
        multiple: {
            // allow uploading multiple files at a time
            type: Boolean,
            default: false,
        },
        deferred: {
            type: Boolean,
            default: false,
        },
        maxFiles: {
            type: Number,
            default: null,
            validator: (value) => value === null || value >= 0,
        },
        maxImageWidth: {
            // max width (if accept == 'image')
            type: Number,
            default: null,
        },
        maxImageHeight: {
            // max height (if accept == 'image')
            type: Number,
            default: null,
        },
        maxFileSizeMb: {
            type: Number,
            default: 25,
        },
        disabled: {
            // input is disabled?
            type: Boolean,
            default: false,
        },
        disabledTooltip: {
            type: String,
            default: '',
        },
        hideUploadField: {
            // hide the upload field
            type: Boolean,
            default: false,
        },
        hideDeleteButton: {
            // hide the delete button
            type: Boolean,
            default: false,
        },
        hideViewButton: {
            // hide the view button
            type: Boolean,
            default: false,
        },
        useCustomPdfViewer: {
            // use the custom pdf viewer
            type: Boolean,
            default: false,
        },
        allowPublicAccess: {
            // if true, prevents app_admin_only from being set (for app-wide content like release notes)
            type: Boolean,
            default: false,
        },
        viewFileHandler: {
            type: Function,
            default: null,
        },
        downloadFileHandler: {
            type: Function,
            default: null,
        },
        deleteFileHandler: {
            type: Function,
            default: null,
        },
        deferredDelete: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localValue: [],
            isModalVisible: false,
            selectedFile: null,
            uploading: false,
            deleting: false,
            deferredFileKeys: new WeakMap(),
            nextDeferredFileKey: 0,
        };
    },
    watch: {
        modelValue(value) {
            this.localValue = this.copyModelValue(value);
        },
        isBusy(newVal) {
            this.$emit('uploading', newVal);
        },
    },
    emits: [
        'update:modelValue',
        'change',
        'click',
        'uploading',
        'limit-exceeded',
    ],
    computed: {
        isBusy() {
            return this.uploading || this.deleting;
        },
        deleteModalTranslationKey() {
            return this.deferredDelete
                ? 'fields.upload.deferredDeleteModal'
                : 'fields.upload.deleteModal';
        },
    },
    methods: {
        copyModelValue(value) {
            const files = Array.isArray(value) ? value : [];
            return this.deferred ? [...files] : structuredClone(files);
        },
        fileKey(file) {
            if (file?.id != null) return file.id;
            if (!this.deferredFileKeys.has(file)) {
                this.deferredFileKeys.set(file, ++this.nextDeferredFileKey);
            }
            return `deferred-file-${this.deferredFileKeys.get(file)}`;
        },
        fileDisplayName(file) {
            return file?.original_filename || file?.name || '';
        },
        deferredFileSelected(file) {
            if (!this.deferred) return;
            const nextLength = this.multiple ? this.localValue.length + 1 : 1;
            if (this.maxFiles !== null && nextLength > this.maxFiles) {
                this.$emit('limit-exceeded', this.maxFiles);
                return;
            }
            this.localValue = this.multiple
                ? [...this.localValue, file]
                : [file];
            this.change(this.localValue);
        },
        removeFile(file, index) {
            if (!this.deferred) {
                this.openModal(file);
                return;
            }
            this.localValue = this.localValue.filter(
                (_candidate, candidateIndex) => candidateIndex !== index
            );
            this.change(this.localValue);
        },
        isImage(file) {
            return file.file_type.startsWith('image');
        },
        fileUploaded(fileResponse, file) {
            // If not multiple, and there is an existing file, replace the existing file with the new one
            if (!fileResponse.success) return;
            if (!this.multiple && this.localValue.length > 0) {
                // Delete the existing file first
                const existingFile = this.localValue[0];
                this.$deleteData('upload/delete', { id: existingFile.id }).then(
                    (response) => {
                        if (response.success) {
                            // Replace the existing file with the new one
                            this.localValue = [
                                {
                                    ...fileResponse.data,
                                    original_filename: file.name,
                                    file_type: file.type,
                                },
                            ];
                            this.change(this.localValue);
                        } else if ('message' in response) {
                            this.$alertStore.pushError(response.message);
                        }
                    }
                );
            } else {
                // Multiple is true or no existing files, just add the new file
                this.localValue.push({
                    ...fileResponse.data,
                    original_filename: file.name,
                    file_type: file.type,
                });
                this.change(this.localValue);
            }
        },

        change(newValue) {
            this.$emit('update:modelValue', newValue);
            this.$emit('change', newValue);
        },

        viewFile(file) {
            if (this.viewFileHandler) {
                this.viewFileHandler(file);
                return;
            }
            const action = getLibraryFileAction(file);

            if (action === 'pdf' && this.useCustomPdfViewer && file?.hash) {
                const routeData = this.$router.resolve({
                    name: 'PdfViewer',
                    params: {
                        hash: file.hash,
                    },
                });
                window.open(routeData.href, '_blank');
                return;
            }

            if (action === 'download') {
                this.$downloadFile('upload/serve', { id: file?.id });
                return;
            }

            this.$getFileBlob({ id: file?.id }).then(([blob]) => {
                const blobUrl = URL.createObjectURL(blob);
                window.open(blobUrl, '_blank');
                window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
            });
        },

        downloadFile(file) {
            if (this.downloadFileHandler) {
                this.downloadFileHandler(file);
                return;
            }
            this.$downloadFile('upload/serve', { id: file.id }).then(() => {
                // Downloaded! Nothing to display here
            });
        },

        async deleteImage() {
            this.deleting = true;
            try {
                if (this.deleteFileHandler) {
                    await this.deleteFileHandler(this.selectedFile);
                    return;
                }
                const response = await this.$deleteData('upload/delete', {
                    id: this.selectedFile.id,
                });
                if (response.success) {
                    const index = this.localValue.findIndex(
                        (file) => file.id == this.selectedFile.id
                    );
                    if (index != -1) {
                        this.localValue.splice(index, 1);
                        this.change(this.localValue);
                        this.$alertStore.pushSuccess(
                            this.$t('fields.upload.deleteSuccess')
                        );
                    }
                } else if ('message' in response) {
                    this.$alertStore.pushError(response.message);
                }
            } finally {
                this.closeModal();
                this.deleting = false;
            }
        },
        openModal(file) {
            this.isModalVisible = true;
            this.selectedFile = file;
        },
        closeModal() {
            this.isModalVisible = false;
            this.selectedFile = null;
        },
    },
    mounted() {
        this.localValue = this.copyModelValue(this.modelValue);
    },
};
</script>

<style scoped>
.file-name {
    font-size: 0.875rem;
    line-height: 1.7;
    color: var(--black);
}
</style>
